import type { SalesMove } from "@sales-ops-amplify/shared";
import { formatDue, formatMoney, priorityLabel } from "./format";

type Props = {
  move: SalesMove;
  index: number;
  totalEligible: number;
  busy?: boolean;
  onPrimary: () => void;
  onReview: () => void;
  onLater: () => void;
  onMore: () => void;
};

export function MobileSalesMoveCard({
  move,
  index,
  totalEligible,
  busy,
  onPrimary,
  onReview,
  onLater,
  onMore,
}: Props) {
  const remaining = Math.max(totalEligible - index, 0);
  const progress =
    totalEligible <= 0
      ? "All clear for today"
      : remaining === 1
        ? "1 move remaining"
        : remaining === totalEligible
          ? `Move ${index + 1} of ${totalEligible}`
          : `Move ${index + 1} of ${totalEligible} · ${remaining} remaining`;

  // Avoid "Move 1 of 1 · 1 move remaining"
  const progressLabel =
    totalEligible === 1
      ? "1 move remaining"
      : totalEligible === 0
        ? "All clear for today"
        : `Move ${index + 1} of ${totalEligible}`;

  return (
    <div className="flex flex-col">
      <p className="mb-3 text-xs font-medium text-muted-foreground">{progressLabel}</p>
      <div className="rounded-lg border border-border bg-card px-4 py-4">
        <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
          Recommended move
        </p>
        <h2 className="mt-1 text-xl font-semibold text-foreground">
          {move.recommendedAction}
        </h2>
        <div className="mt-3 space-y-1 text-sm text-muted-foreground">
          {move.accountName ? <p className="text-foreground">{move.accountName}</p> : null}
          {(move.relevantPerson?.name || move.relevantPerson?.role) && (
            <p>
              {[move.relevantPerson?.name, move.relevantPerson?.role]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {(move.dealStage || move.dealValue != null) && (
            <p>
              {[
                move.dealStage,
                move.dealValue != null
                  ? formatMoney(move.dealValue, move.currency)
                  : null,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
        <p className="mt-4 text-sm text-foreground/90">
          <span className="font-medium text-foreground">Why now · </span>
          {move.whyNow}
        </p>
        {move.expectedOutcome ? (
          <p className="mt-2 text-sm text-foreground/90">
            <span className="font-medium text-foreground">Expected · </span>
            {move.expectedOutcome}
          </p>
        ) : null}
        {move.riskOfWaiting ? (
          <p className="mt-2 text-sm text-foreground/90">
            <span className="font-medium text-foreground">Risk · </span>
            {move.riskOfWaiting}
          </p>
        ) : null}
        <p className="mt-3 text-xs text-muted-foreground">
          {[formatDue(move.dueAt), priorityLabel(move.priority)]
            .filter(Boolean)
            .join(" · ")}
        </p>
        <p className="sr-only">{progress}</p>
      </div>

      <div className="sticky bottom-16 z-10 mt-4 space-y-2 bg-background/95 pb-2 pt-2 backdrop-blur">
        <button
          type="button"
          disabled={busy}
          onClick={onPrimary}
          className="w-full rounded-md bg-brand px-3 py-3 text-sm font-semibold text-brand-foreground disabled:opacity-50"
        >
          {move.actionDefinition?.label || move.recommendedAction}
        </button>
        <div className="grid grid-cols-3 gap-2">
          <button
            type="button"
            onClick={onReview}
            className="rounded-md border border-border py-2 text-xs font-medium"
          >
            Review
          </button>
          <button
            type="button"
            onClick={onLater}
            className="rounded-md border border-border py-2 text-xs font-medium"
          >
            Later
          </button>
          <button
            type="button"
            onClick={onMore}
            className="rounded-md border border-border py-2 text-xs font-medium"
          >
            More
          </button>
        </div>
      </div>
    </div>
  );
}
